import { BooleanKeys } from '@monetize/types/common';
import { QuoteAndContractSettingsConfig } from '@monetize/types/experience-config';

export type QuoteAndContractSettingsBooleanKeys =
  BooleanKeys<QuoteAndContractSettingsConfig>;

export const QuoteAndContractSettingsMetadata: Record<
  keyof QuoteAndContractSettingsConfig,
  { label: string; description?: string }
> = {
  allowRetainApprovalsRoles: {
    label: 'Which Roles Can Retain Approvals When Moving Quote to Edit Mode',
    description:
      'Specifies which user roles are allowed to retain approvals when moving a quote back to edit mode.',
  },
  allowChangingQuoteOfferingStartAndEndDate: {
    label: 'Allow Changing Quote Offering Start and End Date',
    description:
      'Permits editing of quote offering start and end dates during configuration.',
  },
  enableEarlyRenewal: {
    label: 'Enable Early Renewal',
    description:
      'Enables renewals to start before the prior contract ends. The old contract will be cancelled early, which may result in a credit note.',
  },
  enableBillingAnchorAdjustment: {
    label: 'Allow customizing billing start date',
    description:
      'On Quotes, allow the billing start date to be set differently from the contract start date. This determines how the first billing cycle is calculated based on your contract start date and billing frequency.',
  },
  enableUpfrontBilling: {
    label: 'Enable Upfront Billing',
    description:
      'Allows new and renewal quotes to bill the full contract term upfront instead of by the billing frequency of each rate.',
  },
  allowQuantityDropdown: {
    label: 'Allow Quantity Dropdown',
    description:
      'Enables the option to display the quantity field as a dropdown of available tiers on quotes.',
  },
  enablePriceTierLockEligible: {
    label: 'Allow Price Tier Lock',
    description:
      'Allows products in an offering to be marked as price tier lock eligible, locking the price tier for that product on quotes.',
  },
  enableQuoteItemNotes: {
    label: 'Enable Quote Item Notes',
    description: 'Allows users to add notes to individual quote items.',
  },
  enableQuoteOfferingPriceTiers: {
    label: 'Enable Quote Offering Price Tier Visibility Customization',
    description:
      'Allow customization of visible price tiers in customer-facing documents.',
  },
  enableBulkAddQuoteOffering: {
    label: 'Enable Bulk Addition of Quote Offerings',
    description:
      'Allows users to add multiple offerings to a quote simultaneously.',
  },
  hideAutoRenewal: {
    label: 'Disable Auto-Renewal',
    description: 'Removes the option to enable auto-renewal on quotes.',
  },
  hideBillingSchedule: {
    label: 'Disable Billing Schedule',
    description: 'Prevents the billing schedule from being shown on quotes.',
  },
  quoteAcceptanceAllowedRoles: {
    label: 'Which Roles Can Process Quotes',
    description: 'Specifies which user roles are allowed to process quotes.',
  },
  quoteManualAcceptanceAllowedRoles: {
    label: 'Which Roles Can Accept Quotes',
    description:
      'Specifies which user roles are permitted to manually accept quotes.',
  },
  quoteCreateAllowedRoles: {
    label: 'Which Roles Can Create Quotes',
    description:
      'Specifies which user roles are permitted to create new quotes.',
  },
  accountSpecificRateCreation: {
    label: 'Account-Specific Rate Creation',
    description:
      'Allows the creation of rates that are specific to a customer account during the quoting process.',
  },
  accountSpecificRateAllowPercentageAdjustments: {
    label: 'Account-Specific Rate - Percentage Price Change',
    description:
      'Enables percentage price adjustments for account-specific rates during quoting. The percentage increase/decrease is applied to all tiers for the selected products within a rate.',
  },
  accountSpecificRateAllowTierAdjustments: {
    label: 'Account-Specific Rate - Tier Adjustments',
    description:
      'Allows users to adjust the tiers of account-specific rates during quoting.',
  },
  accountSpecificRateAllowPriceAdjustments: {
    label: 'Account-Specific Rate - Price Adjustments',
    description:
      'Allows users to adjust the prices of account-specific rates during quoting. You can disable this and enable "Percentage Price Change" to restrict adjustments to percentage-based changes only.',
  },
  lockPriceModelUpdateOnQuote: {
    label: 'Account-Specific Rate - Price Model Adjustments',
    description:
      'Allow changing the pricing model when customizing a rate during the quoting process.',
  },
  quoteNameEditableRoles: {
    label: 'Which Roles Can Edit Quote Name',
    description:
      'Specifies which user roles are allowed to edit the quote name.',
  },
  quoteShareConfig: {
    label: 'Quote Share',
    description: 'Allow customers to view and accept quotes.',
  },
  showCommaFormattedQty: {
    label: 'Show Comma Formatted Quantity',
    description:
      'Displays quote quantities with comma separators (e.g., 1,000 instead of 1000).',
  },
  showTOCPermission: {
    label: 'Show Quote Terms and Conditions',
    description:
      'Determines visibility of free-form Terms and Conditions to user roles.',
  },
};
